import { formatLabelWithCode } from '../../../../utils/locationFormat';
import type { DashboardGamesRow } from '../../../../types/features/games';
import type { CountryStatesGroup } from '../../../../types/locations';

export type DashboardGamesSortKey =
  | 'stateLabel'
  | 'countryLabel'
  | 'gamesCount'
  | 'gamesWithUpcCount'
  | 'lotterySoftwareProvider';
export type DashboardGamesSortDirection = 'asc' | 'desc';

export const buildDashboardGamesRows = (groupedStates: CountryStatesGroup[]): DashboardGamesRow[] =>
  groupedStates.flatMap((group) => {
    const countryLabel = formatLabelWithCode(group.country_name, group.country_code);

    return group.states.map((state) => ({
      stateCode: state.state_code,
      stateLabel: formatLabelWithCode(state.state_name, state.state_code),
      countryLabel,
      gamesCount: state.games_count ?? 0,
      gamesWithUpcCount: state.games_with_upc_count ?? 0,
      lotterySoftwareProvider: state.lottery_software_provider ?? '',
    }));
  });

export const filterDashboardGamesRows = (
  rows: DashboardGamesRow[],
  searchTerm: string,
  showAllRegions: boolean,
): DashboardGamesRow[] => {
  const normalizedSearchTerm = searchTerm.trim().toLowerCase();

  return rows.filter((row) => {
    if (!showAllRegions && row.gamesCount <= 0) {
      return false;
    }

    if (!normalizedSearchTerm) {
      return true;
    }

    return (
      row.stateLabel.toLowerCase().includes(normalizedSearchTerm)
      || row.countryLabel.toLowerCase().includes(normalizedSearchTerm)
      || row.stateCode.toLowerCase().includes(normalizedSearchTerm)
    );
  });
};

const getDashboardGamesSortValue = (row: DashboardGamesRow, sortKey: DashboardGamesSortKey): string | number => {
  if (sortKey === 'gamesCount' || sortKey === 'gamesWithUpcCount') {
    return row[sortKey];
  }

  return String(row[sortKey]).toLowerCase();
};

export const sortDashboardGamesRows = (
  rows: DashboardGamesRow[],
  sortKey: DashboardGamesSortKey,
  sortDirection: DashboardGamesSortDirection,
): DashboardGamesRow[] => {
  const directionMultiplier = sortDirection === 'asc' ? 1 : -1;

  return [...rows].sort((left, right) => {
    const leftValue = getDashboardGamesSortValue(left, sortKey);
    const rightValue = getDashboardGamesSortValue(right, sortKey);

    if (typeof leftValue === 'number' && typeof rightValue === 'number') {
      return (leftValue - rightValue) * directionMultiplier;
    }

    const primaryCompare = String(leftValue).localeCompare(String(rightValue));

    if (primaryCompare !== 0) {
      return primaryCompare * directionMultiplier;
    }

    if (sortKey === 'countryLabel') {
      return left.stateLabel.localeCompare(right.stateLabel) * directionMultiplier;
    }

    return 0;
  });
};
